import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { SettingsShell } from '~/components/settings/settings-shell'
import { titleFor } from '~/lib/route-title'

export const Route = createFileRoute('/_authenticated/$workspaceSlug/account')({
  component: AccountLayout,
  head: () => ({ meta: [{ title: titleFor(msg`Account`) }] }),
})

type AccountTab = 'profile' | 'appearance' | 'notifications' | 'whatsapp'

const TABS: { id: AccountTab; label: MessageDescriptor }[] = [
  { id: 'profile', label: msg`Profile` },
  { id: 'appearance', label: msg`Appearance` },
  { id: 'notifications', label: msg`Notifications` },
  { id: 'whatsapp', label: msg`WhatsApp` },
]

const TAB_ROUTES = {
  appearance: '/$workspaceSlug/account/appearance',
  notifications: '/$workspaceSlug/account/notifications',
  profile: '/$workspaceSlug/account/profile',
  whatsapp: '/$workspaceSlug/account/whatsapp',
} as const

/**
 * Personal preferences — profile and appearance — reached from
 * the avatar account menu rather than the workspace side nav: these follow
 * the person between workspaces, while everything under Settings is
 * workspace-scoped. The page still lives under the workspace URL so the
 * shell (and its way back) stays around it.
 */
function AccountLayout() {
  const { t, i18n } = useLingui()
  const navigate = useNavigate()
  const { workspaceSlug } = Route.useParams()

  // Each tab is a child route; the pathname's last segment names the tab.
  const activeTab = useRouterState({
    select: (state): AccountTab => {
      const found = TABS.find(({ id }) => state.location.pathname.endsWith(`/${id}`))
      return found?.id ?? 'profile'
    },
  })

  return (
    <SettingsShell
      activeTab={activeTab}
      onTabChange={(id) => {
        const tab = TABS.find((entry) => entry.id === id)
        if (tab !== undefined) {
          void navigate({ params: { workspaceSlug }, to: TAB_ROUTES[tab.id] })
        }
      }}
      tabs={TABS.map(({ id, label }) => ({ id, label: i18n._(label) }))}
      title={t`My account`}
    >
      <Outlet />
    </SettingsShell>
  )
}
